'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function CalorieCalculator() {
  const t = useTranslations("tools.calorieCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>('metric');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    heightCm: 175,
    weightKg: 70,
    heightFt: 5,
    heightIn: 9,
    weightLbs: 154,
    activity: 1.465,
    formula: 'mifflin' as 'mifflin' | 'harris',
    resultUnit: 'calories' as 'calories' | 'kilojoules',
  });
  const [results, setResults] = useState<{
    maintenance: number;
    loss: number;
    gain: number;
    unitText: string;
  } | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' },
      { maxWidth: '100%' },
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        textAlign: 'center' as const,
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    toolHeaderTitle: getResponsiveStyle(
      { margin: 0, fontWeight: 700, color: 'white' },
      { fontSize: '1.5rem' },
      { fontSize: '1.65rem' },
      { fontSize: '1.8rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {},
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    unitTabs: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '2rem',
      borderBottom: '2px solid #e5e7eb',
    },
    unitTab: (active: boolean) => ({
      padding: '0.75rem 1.5rem',
      background: 'none',
      border: 'none',
      borderBottom: active ? '3px solid #2563eb' : '3px solid transparent',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: 600,
      color: active ? '#2563eb' : '#6b7280',
      transition: 'all 0.3s',
    }),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(2, 1fr)' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: {
      fontWeight: 600,
      color: '#374151',
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    input: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s',
    },
    select: {
      padding: '0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s',
      cursor: 'pointer',
    },
    heightInputs: {
      display: 'flex',
      gap: '0.5rem',
    },
    radioGroup: {
      display: 'flex',
      gap: '1.5rem',
      marginTop: '0.5rem',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      fontWeight: 500,
      color: '#374151',
    },
    radio: {
      width: '1.25rem',
      height: '1.25rem',
      cursor: 'pointer',
    },
    advancedSettings: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #e5e7eb',
    },
    settingsToggle: {
      background: 'none',
      border: 'none',
      color: '#2563eb',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '1rem',
      padding: 0,
      marginBottom: '1rem',
    },
    settingsContent: (show: boolean) => ({
      display: show ? 'block' : 'none',
    }),
    calculateBtn: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        fontWeight: 700,
        borderRadius: '0.5rem',
        cursor: 'pointer',
        transition: 'all 0.3s',
        width: '100%',
        marginTop: '1.5rem',
      },
      { padding: '1rem 2rem', fontSize: '1rem' },
      { padding: '1rem 2.5rem', fontSize: '1.05rem' },
      { padding: '1rem 3rem', fontSize: '1.1rem' }
    ),
    results: (show: boolean) => getResponsiveStyle(
      {
        display: show ? 'block' : 'none',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      },
      { padding: '1.5rem' },
      { padding: '1.75rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: '#1e40af', marginTop: 0, marginBottom: '1.5rem' },
      { fontSize: '1.35rem' },
      { fontSize: '1.425rem' },
      { fontSize: '1.5rem' }
    ),
    resultMain: {
      background: 'white',
      padding: '2rem',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      textAlign: 'center' as const,
    },
    resultMainLabel: {
      fontSize: '1.1rem',
      color: '#6b7280',
      fontWeight: 600,
    },
    resultMainValue: getResponsiveStyle(
      { fontWeight: 800, color: '#2563eb', margin: '0.5rem 0' },
      { fontSize: '2.5rem' },
      { fontSize: '2.75rem' },
      { fontSize: '3rem' }
    ),
    resultGoals: getResponsiveStyle(
      { display: 'grid', gap: '1rem', marginTop: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(2, 1fr)' },
      { gridTemplateColumns: 'repeat(3, 1fr)' }
    ),
    resultGoalCard: (type: 'loss' | 'maintain' | 'gain') => ({
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      borderLeft: `4px solid ${type === 'loss' ? '#f59e0b' : type === 'maintain' ? '#2563eb' : '#10b981'}`,
    }),
    resultGoalTitle: {
      fontWeight: 700,
      color: '#374151',
      marginBottom: '0.5rem',
      fontSize: '1.1rem',
    },
    resultGoalValue: (type: 'loss' | 'maintain' | 'gain') => getResponsiveStyle(
      {
        fontWeight: 800,
        margin: '0.5rem 0',
        color: type === 'loss' ? '#f59e0b' : type === 'maintain' ? '#2563eb' : '#10b981',
      },
      { fontSize: '1.6rem' },
      { fontSize: '1.7rem' },
      { fontSize: '1.8rem' }
    ),
    resultGoalDesc: {
      fontSize: '0.85rem',
      color: '#6b7280',
      marginTop: '0.5rem',
    },
    infoNote: {
      background: '#fef3c7',
      borderLeft: '4px solid #f59e0b',
      padding: '1rem',
      marginTop: '1.5rem',
      borderRadius: '0.5rem',
      fontSize: '0.9rem',
      color: '#92400e',
    },
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: 0, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    h3: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '0.75rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.375rem' },
      { fontSize: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, marginBottom: '1rem', paddingLeft: '1.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.0625rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    insightCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(255, 90, 112, 0.05) 0%, rgba(255, 196, 0, 0.05) 100%)',
        borderLeft: '4px solid #ff5a70',
        borderRadius: '0.75rem',
        marginBottom: '1rem',
      },
      { padding: '1rem' },
      { padding: '1.25rem' },
      { padding: '1.5rem' }
    ),
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.05rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.15rem' }
    ),
    reference: {
      marginBottom: '0.75rem',
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      fontWeight: 500,
      transition: 'color 0.3s',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let heightCm: number;
    let weightKg: number;

    if (unitSystem === 'metric') {
      heightCm = inputs.heightCm;
      weightKg = inputs.weightKg;
    } else {
      heightCm = ((inputs.heightFt * 12) + inputs.heightIn) * 2.54;
      weightKg = inputs.weightLbs * 0.453592;
    }

    let bmr: number;
    if (inputs.formula === 'mifflin') {
      if (inputs.gender === 'male') {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) + 5;
      } else {
        bmr = (10 * weightKg) + (6.25 * heightCm) - (5 * inputs.age) - 161;
      }
    } else {
      if (inputs.gender === 'male') {
        bmr = 13.397 * weightKg + 4.799 * heightCm - 5.677 * inputs.age + 88.362;
      } else {
        bmr = 9.247 * weightKg + 3.098 * heightCm - 4.330 * inputs.age + 447.593;
      }
    }

    const maintenance = Math.round(bmr * inputs.activity);
    const loss = Math.round(maintenance - 500);
    const gain = Math.round(maintenance + 500);

    let displayMaintenance: number;
    let displayLoss: number;
    let displayGain: number;
    let unitText: string;

    if (inputs.resultUnit === 'kilojoules') {
      displayMaintenance = Math.round(maintenance * 4.184);
      displayLoss = Math.round(loss * 4.184);
      displayGain = Math.round(gain * 4.184);
      unitText = t('unitKilojoules');
    } else {
      displayMaintenance = maintenance;
      displayLoss = loss;
      displayGain = gain;
      unitText = t('unitCalories');
    }

    setResults({
      maintenance: displayMaintenance,
      loss: displayLoss,
      gain: displayGain,
      unitText,
    });

    setTimeout(() => {
      document.getElementById('results')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.toolHeaderTitle}>{t('toolTitle')}</h2>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.unitTabs}>
              <button
                type="button"
                style={styles.unitTab(unitSystem === 'metric')}
                onClick={() => setUnitSystem('metric')}
              >
                {t('metricUnits')}
              </button>
              <button
                type="button"
                style={styles.unitTab(unitSystem === 'imperial')}
                onClick={() => setUnitSystem('imperial')}
              >
                {t('imperialUnits')}
              </button>
            </div>

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="age">{t('ageLabel')}</label>
                <input
                  style={styles.input}
                  type="number"
                  id="age"
                  min="15"
                  max="80"
                  value={inputs.age}
                  onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>{t('genderLabel')}</label>
                <div style={styles.radioGroup}>
                  <label style={styles.radioLabel}>
                    <input
                      style={styles.radio}
                      type="radio"
                      name="gender"
                      value="male"
                      checked={inputs.gender === 'male'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                    />
                    {t('genderMale')}
                  </label>
                  <label style={styles.radioLabel}>
                    <input
                      style={styles.radio}
                      type="radio"
                      name="gender"
                      value="female"
                      checked={inputs.gender === 'female'}
                      onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                    />
                    {t('genderFemale')}
                  </label>
                </div>
              </div>
            </div>

            {unitSystem === 'metric' ? (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="height-cm">{t('heightCmLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="height-cm"
                    min="100"
                    max="250"
                    step="0.1"
                    value={inputs.heightCm}
                    onChange={(e) => setInputs({...inputs, heightCm: parseFloat(e.target.value)})}
                    required
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="weight-kg">{t('weightKgLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="weight-kg"
                    min="30"
                    max="300"
                    step="0.1"
                    value={inputs.weightKg}
                    onChange={(e) => setInputs({...inputs, weightKg: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>
            ) : (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('heightLabel')}</label>
                  <div style={styles.heightInputs}>
                    <input
                      style={{...styles.input, flex: 1}}
                      type="number"
                      id="height-ft"
                      min="3"
                      max="8"
                      value={inputs.heightFt}
                      onChange={(e) => setInputs({...inputs, heightFt: parseInt(e.target.value)})}
                      placeholder={t('feetPlaceholder')}
                    />
                    <input
                      style={{...styles.input, flex: 1}}
                      type="number"
                      id="height-in"
                      min="0"
                      max="11"
                      value={inputs.heightIn}
                      onChange={(e) => setInputs({...inputs, heightIn: parseInt(e.target.value)})}
                      placeholder={t('inchesPlaceholder')}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="weight-lbs">{t('weightLbsLabel')}</label>
                  <input
                    style={styles.input}
                    type="number"
                    id="weight-lbs"
                    min="66"
                    max="660"
                    step="0.1"
                    value={inputs.weightLbs}
                    onChange={(e) => setInputs({...inputs, weightLbs: parseFloat(e.target.value)})}
                    required
                  />
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="activity">{t('activityLabel')}</label>
              <select
                style={styles.select}
                id="activity"
                value={inputs.activity}
                onChange={(e) => setInputs({...inputs, activity: parseFloat(e.target.value)})}
                required
              >
                <option value="1.2">{t('activitySedentary')}</option>
                <option value="1.375">{t('activityLight')}</option>
                <option value="1.465">{t('activityModerate')}</option>
                <option value="1.55">{t('activityActive')}</option>
                <option value="1.725">{t('activityVeryActive')}</option>
                <option value="1.9">{t('activityExtraActive')}</option>
              </select>
            </div>

            <div style={styles.advancedSettings}>
              <button
                type="button"
                style={styles.settingsToggle}
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? t('hideAdvanced') : t('showAdvanced')}
              </button>
              <div style={styles.settingsContent(showAdvanced)}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('formulaLabel')}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        style={styles.radio}
                        type="radio"
                        name="formula"
                        value="mifflin"
                        checked={inputs.formula === 'mifflin'}
                        onChange={(e) => setInputs({...inputs, formula: e.target.value as 'mifflin' | 'harris'})}
                      />
                      {t('formulaMifflin')}
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        style={styles.radio}
                        type="radio"
                        name="formula"
                        value="harris"
                        checked={inputs.formula === 'harris'}
                        onChange={(e) => setInputs({...inputs, formula: e.target.value as 'mifflin' | 'harris'})}
                      />
                      {t('formulaHarris')}
                    </label>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>{t('resultUnitLabel')}</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input
                        style={styles.radio}
                        type="radio"
                        name="unit"
                        value="calories"
                        checked={inputs.resultUnit === 'calories'}
                        onChange={(e) => setInputs({...inputs, resultUnit: e.target.value as 'calories' | 'kilojoules'})}
                      />
                      {t('resultUnitCalories')}
                    </label>
                    <label style={styles.radioLabel}>
                      <input
                        style={styles.radio}
                        type="radio"
                        name="unit"
                        value="kilojoules"
                        checked={inputs.resultUnit === 'kilojoules'}
                        onChange={(e) => setInputs({...inputs, resultUnit: e.target.value as 'calories' | 'kilojoules'})}
                      />
                      {t('resultUnitKilojoules')}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={styles.calculateBtn}>
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div id="results" style={styles.results(true)}>
              <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>

              <div style={styles.resultMain}>
                <div style={styles.resultMainLabel}>{t('maintenanceLabel')}</div>
                <div style={styles.resultMainValue}>{results.maintenance.toLocaleString()}</div>
                <div style={styles.resultMainLabel}>{results.unitText}</div>
              </div>

              <div style={styles.resultGoals}>
                <div style={styles.resultGoalCard('loss')}>
                  <div style={styles.resultGoalTitle}>{t('weightLossTitle')}</div>
                  <div style={styles.resultGoalValue('loss')}>{results.loss.toLocaleString()}</div>
                  <div style={styles.resultGoalDesc}>{t('weightLossDesc')}</div>
                </div>

                <div style={styles.resultGoalCard('maintain')}>
                  <div style={styles.resultGoalTitle}>{t('maintainWeightTitle')}</div>
                  <div style={styles.resultGoalValue('maintain')}>{results.maintenance.toLocaleString()}</div>
                  <div style={styles.resultGoalDesc}>{t('maintainWeightDesc')}</div>
                </div>

                <div style={styles.resultGoalCard('gain')}>
                  <div style={styles.resultGoalTitle}>{t('weightGainTitle')}</div>
                  <div style={styles.resultGoalValue('gain')}>{results.gain.toLocaleString()}</div>
                  <div style={styles.resultGoalDesc}>{t('weightGainDesc')}</div>
                </div>
              </div>

              <div style={styles.infoNote}>
                <strong>{t('noteTitle')}</strong> {t('noteText')}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToStep1')}</li>
          <li style={styles.listItem}>{t('howToStep2')}</li>
          <li style={styles.listItem}>{t('howToStep3')}</li>
          <li style={styles.listItem}>{t('howToStep4')}</li>
          <li style={styles.listItem}>{t('howToStep5')}</li>
          <li style={styles.listItem}>{t('howToStep6')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight1Title')}</h3>
          <p style={styles.paragraph}>{t('insight1Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight2Title')}</h3>
          <p style={styles.paragraph}>{t('insight2Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight3Title')}</h3>
          <p style={styles.paragraph}>{t('insight3Text')}</p>
        </div>
        <div style={styles.insightCard}>
          <h3 style={styles.h3}>{t('insight4Title')}</h3>
          <p style={styles.paragraph}>{t('insight4Text')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <h3 style={styles.h3}>{t('detailsBmrTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsBmrText')}</p>

        <h3 style={styles.h3}>{t('detailsTdeeTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsTdeeText')}</p>

        <h3 style={styles.h3}>{t('detailsFormulasTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsFormulasText')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('detailsFormulasMifflin')}</li>
          <li style={styles.listItem}>{t('detailsFormulasHarris')}</li>
        </ul>

        <h3 style={styles.h3}>{t('detailsActivityTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsActivityText')}</p>

        <h3 style={styles.h3}>{t('detailsGoalsTitle')}</h3>
        <p style={styles.paragraph}>{t('detailsGoalsText')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq1Question')}</div>
          <p style={styles.paragraph}>{t('faq1Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq2Question')}</div>
          <p style={styles.paragraph}>{t('faq2Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq3Question')}</div>
          <p style={styles.paragraph}>{t('faq3Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq4Question')}</div>
          <p style={styles.paragraph}>{t('faq4Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq5Question')}</div>
          <p style={styles.paragraph}>{t('faq5Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <div style={styles.faqQuestion}>{t('faq6Question')}</div>
          <p style={styles.paragraph}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <div style={styles.reference}>
          <a
            href="https://nutriscan.app/calculators/online-calorie-calculator"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference1')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://caloriecalculator.cloud/benefits-of-calculators-for-nutrition-apps/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference2')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://codiant.com/blog/develop-a-calorie-counting-app-cost-and-key-features/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference3')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://www.peqish.com/health-and-wellness-blog/top-food-in-calories-calculator-track-amp-achieve-your-nutrition-goals"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference4')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://wpcaloriecalculator.com/blog/benefits-of-calorie-calculators-for-fitness-websites/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference5')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://cronometer.com/index.html"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference6')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://alphaprogression.com/en/blog/counting-calories"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference7')}
          </a>
        </div>
        <div style={styles.reference}>
          <a
            href="https://beyondtype1.org/calorie-counting-apps/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.referenceLink}
          >
            {t('reference8')}
          </a>
        </div>
      </section>
    </div>
  );
}